import _ from "lodash"
import {toast} from "react-toastify"
import TomClient from "../services/tomClient"
import {registerDefaultsFromResponse} from "../misc/default-registry"


const STAGING_AREA_LINKS_POINTER = "/links"

const buildLinkName = (prefix, contextArea) => `${prefix}${contextArea}`

const CONTEXT_AREA_ROOT_PATTERNS = [
    /^kunde$/,
    /^ehepartner$/,
    /^berechnungsVorgaben/,
    /^berechnungsvorgaben/,
    /^kinder/,
]

const isContextAreaRoot = (rootSegment) =>
    CONTEXT_AREA_ROOT_PATTERNS.some((pattern) => pattern.test(rootSegment))


export const buildChangeFromPointer = (pointer, value) => {
    const segments = pointer?.split("/").filter(Boolean) ?? []
    if (!segments.length) return {}
    return _.set({}, segments, value)
}

export const contextRootNames = (pointer, getDataFromPointer) => {
    const roots = Object.keys(getDataFromPointer("") ?? {}).filter(isContextAreaRoot)
    const changedRoot = pointer?.split("/").filter(Boolean)[0]
    if (changedRoot && !roots.includes(changedRoot)) roots.push(changedRoot)
    return roots
}

export const buildContextFromStore = (getDataFromPointer) => {
    const rootData = getDataFromPointer("") ?? {}
    const context = {}
    for (const root of Object.keys(rootData)) {
        if (isContextAreaRoot(root)) context[root] = _.cloneDeep(rootData[root])
    }
    return context
}


export const buildBodyFromPointer = (pointer, value, getDataFromPointer) => {
    const segments = pointer?.split("/").filter(Boolean) ?? []
    const rootData = getDataFromPointer("") ?? {}

    const body = {}
    for (const root of Object.keys(rootData)) {
        if (isContextAreaRoot(root)) {
            body[root] = _.cloneDeep(rootData[root])
        }
    }

    if (segments.length > 0) {
        _.set(body, segments, value)
    }

    return body
}

const buildCalcActionName = (contextArea) => `CALC_CONTEXTAREA_${contextArea.toUpperCase()}`

const safeTriggerCalc = (triggerAction, contextArea, updateResponse) => {
    const calcActionName = buildCalcActionName(contextArea)
    try {
        return Promise.resolve(triggerAction(calcActionName, updateResponse))
    } catch (err) {
        console.warn(`[UPDATE_CONTEXTAREA] keine Calc-Action '${calcActionName}' definiert — überspringe`)
        return Promise.resolve()
    }
}

const activeContextAreas = new Map()
const areaContainerIds = new Map()

const registerActiveArea = (contextArea, containerId) => {
    if (!contextArea) return
    activeContextAreas.set(contextArea, (activeContextAreas.get(contextArea) ?? 0) + 1)
    if (containerId) areaContainerIds.set(contextArea, containerId)
}

const unregisterActiveArea = (contextArea) => {
    if (!contextArea) return
    const next = (activeContextAreas.get(contextArea) ?? 0) - 1
    if (next <= 0) {
        activeContextAreas.delete(contextArea)
        areaContainerIds.delete(contextArea)
    } else {
        activeContextAreas.set(contextArea, next)
    }
}

const arrayOverwriteCustomizer = (_objValue, srcValue) => {
    if (Array.isArray(srcValue)) return srcValue
    return undefined
}

const DERIVED_ROOT_KEYS = new Set(["ergebnisVersorgungslueckenVerlauf"])

const reportOverwrittenValues = (rootKey, previousData, nextData) => {
    const losses = []
    const walk = (previousValue, nextValue, path) => {
        if (previousValue === null || previousValue === undefined) return
        if (_.isPlainObject(previousValue)) {
            for (const [key, value] of Object.entries(previousValue)) {
                walk(value, _.isObject(nextValue) ? nextValue[key] : undefined, `${path}/${key}`)
            }
            return
        }
        if (Array.isArray(previousValue)) {
            previousValue.forEach((value, index) => {
                walk(
                    value,
                    Array.isArray(nextValue) ? nextValue[index] : undefined,
                    `${path}/${index}`,
                )
            })
            return
        }
        if (previousValue === "" || _.isEqual(previousValue, nextValue)) return
        losses.push({pointer: path, previousValue, nextValue})
    }
    walk(previousData, nextData, `/${rootKey}`)
    if (losses.length) console.debug("[enrich/merge] überschriebene Werte:", losses)
}

const mergeDataIntoRoot = (responseData, {getDataFromPointer, updateData}) => {
    if (!responseData || typeof responseData !== "object") return
    for (const [key, value] of Object.entries(responseData)) {
        if (DERIVED_ROOT_KEYS.has(key)) continue
        const pointer = `/${key}`
        if (Array.isArray(value) || value === null || typeof value !== "object") {
            reportOverwrittenValues(key, getDataFromPointer(pointer), value)
            updateData(value, pointer)
            continue
        }
        const current = getDataFromPointer(pointer)
        const base = current && typeof current === "object" && !Array.isArray(current)
            ? current
            : {}
        const merged = _.mergeWith({}, base, value, arrayOverwriteCustomizer)
        reportOverwrittenValues(key, current, merged)
        updateData(merged, pointer)
    }
}

const messageSeverityToToast = {
    info: toast.info,
    debug: toast.info,
    warn: toast.warning,
    warning: toast.warning,
    error: toast.error,
    fatal: toast.error,
}

const showEnricherMessages = (messages) => {
    if (!Array.isArray(messages)) return
    for (const message of messages) {
        const show = messageSeverityToToast[String(message?.severity).toLowerCase()]
        if (show && message?.text) show(message.text, {toastId: message.text})
    }
}

const triggerAreaCalcs = (data, deps, areas) => {
    const triggerAction = deps?.triggerAction
    if (!triggerAction) return
    for (const area of areas ?? [...activeContextAreas.keys()]) {
        safeTriggerCalc(triggerAction, area, {calcData: data ?? {}})
    }
}

const isValidLink = (link) =>
    !!link && typeof link === "object" && typeof link.href === "string"

const stagingAreaLinks = (getDataFromPointer) =>
    Object.fromEntries(
        Object.entries(getDataFromPointer(STAGING_AREA_LINKS_POINTER) ?? {}).filter(
            ([, link]) => isValidLink(link),
        ),
    )

const mergeLinksIntoStore = (links, {getDataFromPointer, updateData}) => {
    if (!links || typeof links !== "object") return
    updateData({...stagingAreaLinks(getDataFromPointer), ...links}, STAGING_AREA_LINKS_POINTER)
}


const activeCalcLinks = (getDataFromPointer) => {
    const links = stagingAreaLinks(getDataFromPointer)
    const out = {}
    for (const area of activeContextAreas.keys()) {
        const link = links[buildLinkName("calculate", area)]
        if (link?.href) out[area] = link
    }
    return out
}

let storeRevision = 0

const nextRevision = () => ++storeRevision

const isOutdated = (revision, source) => {
    if (revision === storeRevision) return false
    console.debug(
        `[${source}] Antwort zu Revision ${revision} ist ueberholt (aktuell ${storeRevision}) - Merge uebersprungen`,
    )
    return true
}

const ENRICH_BATCH_MS = 50
let enrichBatch = {areas: new Set(), containerId: null, deps: null, promise: null}

let laufendeDatenabfrage = null

const merkeDatenabfrage = (anfrage) => {
    const beendet = anfrage.then(
        () => undefined,
        () => undefined,
    )
    laufendeDatenabfrage = beendet
    beendet.then(() => {
        if (laufendeDatenabfrage === beendet) laufendeDatenabfrage = null
    })
    return anfrage
}

const flushEnrichBatch = () => {
    const {areas, containerId, deps} = enrichBatch
    enrichBatch = {areas: new Set(), containerId: null, deps: null, promise: null}
    const {getDataFromPointer, updateData} = deps ?? {}
    const revision = storeRevision
    return TomClient.postEnrich("/data", {
        areas: [...areas],
        containerId,
        links: stagingAreaLinks(getDataFromPointer),
    }).then((response) => {
        registerDefaultsFromResponse(response)
        mergeLinksIntoStore(response?.links, {getDataFromPointer, updateData})
        showEnricherMessages(response?.messages)
        if (isOutdated(revision, "enrich/data")) return response
        mergeDataIntoRoot(response?.data, deps)
        triggerAreaCalcs(response?.data, deps, response?.areas)
        return response
    })
}

const enrichFetch = ({contextArea, containerId}, deps) => {
    enrichBatch.areas.add(contextArea)
    if (containerId) enrichBatch.containerId = containerId
    enrichBatch.deps = deps
    if (!enrichBatch.promise) {
        enrichBatch.promise = merkeDatenabfrage(
            new Promise((resolve, reject) => {
                setTimeout(() => flushEnrichBatch().then(resolve, reject), ENRICH_BATCH_MS)
            }),
        )
    }
    return enrichBatch.promise
}


const awaitEnrich = () => laufendeDatenabfrage ?? Promise.resolve()

const awaitLinks = (getDataFromPointer) => {
    if (Object.keys(stagingAreaLinks(getDataFromPointer)).length) return Promise.resolve()
    return awaitEnrich()
}


const saveChainPerArea = new Map()

const inSaveChain = (contextArea, task) => {
    const previousCall = saveChainPerArea.get(contextArea) ?? Promise.resolve()
    const currentCall = previousCall.then(task, task)
    saveChainPerArea.set(
        contextArea,
        currentCall.then(
            () => undefined,
            () => undefined,
        ),
    )
    return currentCall
}

const enrichSaveBody = (
    {contextArea, containerId, change, changedPointer, roots},
    deps,
) => inSaveChain(contextArea, () => {
    const {getDataFromPointer, updateData} = deps
    return awaitLinks(getDataFromPointer).then(() => {
        const links = stagingAreaLinks(getDataFromPointer)
        if (!links[buildLinkName("update", contextArea)]?.href) {
            console.warn(
                `[enrich/value] Link '${buildLinkName("update", contextArea)}' fehlt unter ${STAGING_AREA_LINKS_POINTER} — Staging Area nicht initialisiert?`,
            )
            return Promise.resolve()
        }
        const revision = nextRevision()
        return TomClient.postEnrich("/value", {
            contextArea,
            containerId: containerId ?? null,
            links,
            context: buildContextFromStore(getDataFromPointer),
            change,
            changedPointer: changedPointer ?? null,
            roots,
            activeAreas: activeCalcLinks(getDataFromPointer),
        }).then((response) => {
            mergeLinksIntoStore(response?.links, {getDataFromPointer, updateData})
            showEnricherMessages(response?.messages)
            if (isOutdated(revision, "enrich/value")) return response
            mergeDataIntoRoot(response?.data, deps)
            triggerAreaCalcs(response?.data, deps, response?.recalculatedAreas)
            return response
        })
    })
})

const enrichSave = ({contextArea, containerId, pointer, value}, deps) =>
    enrichSaveBody(
        {
            contextArea,
            containerId,
            change: buildChangeFromPointer(pointer, value),
            changedPointer: pointer ?? null,
            roots: contextRootNames(pointer, deps.getDataFromPointer),
        },
        deps,
    )

const SEED_BATCH_MS = 60

const seededPointers = new Set()

let seedBatch = {entries: new Map(), deps: null, promise: null}

const seedRoots = (change, getDataFromPointer) => {
    const roots = contextRootNames(null, getDataFromPointer)
    for (const root of Object.keys(change)) {
        if (!roots.includes(root)) roots.push(root)
    }
    return roots
}

const fillArrayGaps = (node) => {
    if (Array.isArray(node)) {
        for (let index = 0; index < node.length; index++) {
            if (!(index in node)) node[index] = {}
            else fillArrayGaps(node[index])
        }
        return node
    }
    if (_.isPlainObject(node)) {
        for (const value of Object.values(node)) fillArrayGaps(value)
    }
    return node
}

const flushSeedBatch = () => {
    const {entries, deps} = seedBatch
    seedBatch = {entries: new Map(), deps: null, promise: null}
    const {getDataFromPointer, updateData} = deps ?? {}
    if (!getDataFromPointer || !updateData) return Promise.resolve()

    return awaitEnrich().then(() => {
        const offen = [...entries.values()].filter(
            (eintrag) =>
                !seededPointers.has(eintrag.pointer) &&
                getDataFromPointer(eintrag.pointer) == null,
        )
        if (!offen.length) return undefined

        const changeByArea = new Map()
        for (const {pointer, value, contextArea} of offen) {
            seededPointers.add(pointer)
            updateData(value, pointer)
            if (!contextArea) continue
            if (!changeByArea.has(contextArea)) changeByArea.set(contextArea, {})
            _.set(changeByArea.get(contextArea), pointer.split("/").filter(Boolean), value)
        }

        return [...changeByArea].reduce(
            (kette, [contextArea, change]) =>
                kette.then(() =>
                    enrichSaveBody(
                        {
                            contextArea,
                            change: fillArrayGaps(change),
                            changedPointer: null,
                            roots: seedRoots(change, getDataFromPointer),
                        },
                        deps,
                    ),
                ),
            Promise.resolve(),
        )
    })
}


const seedDefault = ({pointer, value, contextArea}, deps) => {
    if (!pointer || seededPointers.has(pointer)) return Promise.resolve()
    seedBatch.entries.set(pointer, {pointer, value, contextArea: contextArea ?? null})
    seedBatch.deps = deps
    if (!seedBatch.promise) {
        seedBatch.promise = new Promise((resolve, reject) => {
            setTimeout(() => flushSeedBatch().then(resolve, reject), SEED_BATCH_MS)
        })
    }
    return seedBatch.promise
}

export const ContextAreaActions = ({
    getDataFromPointer,
    updateData,
    getDataFromBackend,
    triggerAction,
}) => ({

    INIT_STAGING_AREA: ({stagingArea}) => {
        if (!stagingArea?.href) {
            console.warn("[INIT_STAGING_AREA] kein createStagingArea-Link übergeben")
            return Promise.resolve()
        }
        seededPointers.clear()
        const mergeLinks = (_data, newLinks) => ({
            ...stagingAreaLinks(getDataFromPointer),
            ...newLinks,
        })
        return getDataFromBackend(stagingArea, STAGING_AREA_LINKS_POINTER, mergeLinks).then(
            (response) => {
                return response
            },
        )
    },
    LOAD_PAGE: ({containerId}) => {
        const revision = storeRevision
        return merkeDatenabfrage(
            TomClient.postEnrich("/data", {
                containerId,
                links: stagingAreaLinks(getDataFromPointer),
            }).then((response) => {
                registerDefaultsFromResponse(response)
                mergeLinksIntoStore(response?.links, {getDataFromPointer, updateData})
                showEnricherMessages(response?.messages)
                if (isOutdated(revision, "enrich/data")) return response
                mergeDataIntoRoot(response?.data, {getDataFromPointer, updateData})
                for (const area of response?.areas ?? []) {
                    safeTriggerCalc(triggerAction, area, {calcData: response?.data})
                }
                return response
            }),
        )
    },
    GET_CONTEXTAREA: ({contextArea, containerId}) => {
        if (!contextArea) {
            console.warn("[GET_CONTEXTAREA] kein contextArea übergeben")
            return
        }
        return enrichFetch(
            {contextArea, containerId},
            {getDataFromPointer, updateData, triggerAction},
        )
    },
    REGISTER_CONTEXTAREA: ({contextArea, containerId}) => registerActiveArea(contextArea, containerId),
    UNREGISTER_CONTEXTAREA: ({contextArea}) => unregisterActiveArea(contextArea),
    RESET_CONTEXTAREAS: () => {
        activeContextAreas.clear()
        seededPointers.clear()
    },
    REFRESH_ALL_CONTEXTAREAS: () =>
        Promise.all(
            [...activeContextAreas.keys()].map((area) =>
                enrichFetch(
                    {contextArea: area},
                    {getDataFromPointer, updateData, triggerAction},
                ),
            ),
        ),
    SEED_DEFAULT: ({pointer, value, contextArea}) =>
        seedDefault(
            {pointer, value, contextArea},
            {getDataFromPointer, updateData, triggerAction},
        ),
    UPDATE_CONTEXTAREA: ({contextArea, containerId, value, merkmal}) => {
        if (!contextArea) {
            console.warn("[UPDATE_CONTEXTAREA] kein contextArea übergeben")
            return
        }
        return enrichSave(
            {
                contextArea,
                containerId: containerId ?? areaContainerIds.get(contextArea),
                pointer: merkmal?.pointer,
                value,
            },
            {getDataFromPointer, updateData, triggerAction},
        )
    },
})
